(function (scope) {
  const familyOrder = [
    'white',
    'black-grey',
    'red-pink',
    'blue',
    'green',
    'yellow-orange',
    'purple',
    'brown-bronze',
  ];

  function normalizeValue(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function classifyColourFamily(value) {
    const name = normalizeValue(value);
    const includesAny = (...terms) => terms.some((term) => name.includes(term));

    if (includesAny('white', 'ivory', 'cream')) return 'white';
    if (includesAny('black', 'grey', 'gray', 'silver', 'graphite', 'carbon', 'charcoal')) return 'black-grey';
    if (includesAny('red', 'pink', 'rose', 'burgundy', 'cherry', 'maroon', 'magenta')) return 'red-pink';
    if (includesAny('blue', 'navy', 'cyan', 'teal', 'aqua')) return 'blue';
    if (includesAny('green', 'emerald', 'olive', 'mint', 'jade')) return 'green';
    if (includesAny('yellow', 'orange', 'gold', 'lemon', 'amber')) return 'yellow-orange';
    if (includesAny('purple', 'violet', 'lavender', 'lilac', 'plum')) return 'purple';
    if (includesAny('brown', 'bronze', 'copper', 'champagne', 'beige', 'coffee')) return 'brown-bronze';
    return 'other';
  }

  function resolveColourFamily(product) {
    const explicitFamily = normalizeValue(product.family);
    if (familyOrder.includes(explicitFamily) || explicitFamily === 'other') return explicitFamily;
    if (explicitFamily) return classifyColourFamily(explicitFamily);
    return classifyColourFamily(product.name);
  }

  function matchesColour(product, queryValue, activeFamily = 'all') {
    const query = normalizeValue(queryValue);
    const name = normalizeValue(product.name);
    const code = normalizeValue(product.code);
    const family = resolveColourFamily(product);
    const queryMatches = query === '' || name.includes(query) || code === query;
    const familyMatches = activeFamily === 'all' || family === activeFamily;
    return queryMatches && familyMatches;
  }

  class ColourLibrary {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-colour-search]');
      this.cards = [...root.querySelectorAll('[data-colour-card]')];
      this.filters = [...root.querySelectorAll('[data-colour-family-filter]')];
      this.status = root.querySelector('[aria-live]');
      this.activeFamily = 'all';

      if (!this.input) return;
      this.prepareFamilies();
      this.input.addEventListener('input', () => this.filter());
      this.filters.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFamily = button.dataset.colourFamilyFilter || 'all';
          this.filters.forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
          this.filter();
        });
      });
    }

    prepareFamilies() {
      const counts = new Map();
      this.cards.forEach((card) => {
        const family = resolveColourFamily({
          name: card.dataset.colourName,
          family: card.dataset.colourFamily,
        });
        card.dataset.colourFamilyResolved = family;
        counts.set(family, (counts.get(family) || 0) + 1);
      });

      this.filters.forEach((button) => {
        const family = button.dataset.colourFamilyFilter;
        if (family !== 'all') button.hidden = !counts.get(family);
      });
    }

    filter() {
      let visible = 0;

      this.cards.forEach((card) => {
        const matches = matchesColour({
          name: card.dataset.colourName,
          code: card.dataset.colourCode,
          family: card.dataset.colourFamilyResolved,
        }, this.input.value, this.activeFamily);
        card.closest('li').hidden = !matches;
        if (matches) visible += 1;
      });

      this.status.hidden = false;
      this.status.textContent = visible === 0
        ? 'No colours on this page match. Press Enter to search the complete catalogue.'
        : `${visible} ${visible === 1 ? 'colour' : 'colours'} shown on this page.`;
    }
  }

  const initializedLibraries = new WeakSet();

  const initializeColourLibraries = (root = scope.document) => {
    root?.querySelectorAll?.('[data-colour-library]').forEach((libraryRoot) => {
      if (initializedLibraries.has(libraryRoot)) return;
      initializedLibraries.add(libraryRoot);
      new ColourLibrary(libraryRoot);
    });
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ColourLibrary, classifyColourFamily, matchesColour, normalizeValue, resolveColourFamily };
  }

  if (scope.document) {
    initializeColourLibraries();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.('[data-colour-library]')) initializeColourLibraries(node.parentElement);
          else initializeColourLibraries(node);
        });
      });
    }).observe(scope.document.documentElement, { childList: true, subtree: true });
  }
})(typeof window !== 'undefined' ? window : globalThis);
